// undoRejectText — conflict-aware Undo (β Modern) の拒否 reason → 日本語文言ヘルパ。
//
// (docs/_wip/undo-conflict-aware-2026-07-12.md「Undo 拒否時の UI — 初期スコープに
// 昇格」/ protocol-events.md §1.1 `stroke.cancel-rejected` が正)
//
// `imagepasteReject.ts` と同じ役割分担: reason→文言の純関数をここに集約し、UI 層
// (`useConflictAwareUndo` + `ImagepasteToastOverlay` 再利用) はキャンバス中央トーストに
// 流すだけにする。
//
// `conflict` だけは静的文言でなく、交差相手 (`conflicts[].uid`) を表示名解決して
// 埋め込む (拒否 UI のハイライト用途、§1.1)。名前解決は呼び出し側の
// `resolveDisplayName` 越し (= 純関数のまま、React / members state に依存しない)。

import type { StrokeCancelRejected, StrokeCancelRejectReason } from '@oekaki/protocol';

/** reason → 静的な日本語文言 (`conflict` は動的に組むので別扱い)。 */
const STATIC_REASON_TEXT: Readonly<Record<Exclude<StrokeCancelRejectReason, 'conflict'>, string>> =
  {
    'too-old': '時間が経ったため取り消せません',
    locked: '処理中のため少し待ってください',
    // rate-limited は Ctrl+Z 連打が正常系である以上まず出ないが (20回/10秒)、
    // 出たときは「少し待てば戻る」と分かる文言にする (retryAfterMs があれば秒数を
    // 添える — 下の buildRateLimitedText 参照)。
    'rate-limited': '操作が多すぎます。少し待ってからお試しください',
    // not-ended / not-owner は通常の UI 操作では起こらない防御的 reason
    // (docs 「通常 UI では発生しない防御」) — 素朴な文言で十分。
    'not-ended': '描いている途中のため取り消せません',
    'not-owner': '自分が描いた線だけ取り消せます',
  };

const FALLBACK_CONFLICT_TEXT = '上に描かれているため取り消せません';

/**
 * conflict 拒否に添える一言 (2026-10-26)。上に他の線が重なって戻せないとき、消しゴムなら
 * 消せることを子どもにも分かる平易な言葉で伝える。消しゴムは選んでいるレイヤーにしか
 * 効かないので、誤解を招かないよう「その線があるレイヤーで」と添える。
 */
const ERASER_HINT = '\n消しゴムなら消せるよ (その線のあるレイヤーで)';

/** 交差相手の名前をトーストに列挙する上限 (超過分は「ほか N 人」に畳む)。 */
const MAX_CONFLICT_NAMES = 3;

/**
 * `stroke.cancel-rejected` payload (`reason` + 任意の `conflicts`) → トースト文言。
 *
 * `resolveDisplayName` は conflicts の各 `uid` を表示名へ解決する。2026-07-22
 * 「交差対象は自他問わず」により **自分自身の uid が conflicts に入ることもある**
 * (= 自分の後続 stroke と交差した self-歯抜けケース) — 呼び出し側で「自分」等の
 * 特別表示に倒してよい。解決できない uid (退室済み等) は `null` を返せば
 * 「名前不明のユーザー」にフォールバックする。
 */
export const undoRejectText = (
  payload: Pick<StrokeCancelRejected, 'reason' | 'conflicts' | 'retryAfterMs'>,
  resolveDisplayName: (uid: string) => string | null,
): string => {
  if (payload.reason === 'rate-limited') {
    // retryAfterMs は rate-limited のときだけ載る (imagepaste-rejected と同慣習)。
    // 切り上げ秒で「あと N 秒」を添える。未指定なら静的文言のまま。
    const ms = payload.retryAfterMs;
    if (ms === undefined || ms <= 0) return STATIC_REASON_TEXT['rate-limited'];
    return `操作が多すぎます。あと${Math.ceil(ms / 1000)}秒お待ちください`;
  }
  if (payload.reason !== 'conflict') return STATIC_REASON_TEXT[payload.reason];

  const conflicts = payload.conflicts ?? [];
  if (conflicts.length === 0) return FALLBACK_CONFLICT_TEXT + ERASER_HINT;

  // 同一 uid が複数 conflict に出ても名前は 1 回だけ (= 出現順を保ったまま重複除去)。
  const names: string[] = [];
  for (const c of conflicts) {
    const name = resolveDisplayName(c.uid) ?? '名前不明のユーザー';
    if (!names.includes(name)) names.push(name);
  }

  const shown = names.slice(0, MAX_CONFLICT_NAMES);
  const rest = names.length - shown.length;
  const namesText = rest > 0 ? `${shown.join('、')} ほか${rest}人` : shown.join('、');
  return `${namesText}が上に描いているため取り消せません${ERASER_HINT}`;
};
